import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Copy, Trash2, Mail, UserCheck, UserCog, Check, Users, Send, Pencil } from "lucide-react";
import { formatDate } from "@/lib/format";
import { useConfirm } from "@/context/ConfirmContext";

// Assignable page permissions — must match STAFF_PERMISSIONS on the
// backend. Settings and Staff are intentionally absent because they
// are admin-only by design.
const PAGE_PERMISSIONS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "quotes", label: "Quotes" },
  { key: "clients", label: "Clients" },
  { key: "vessels", label: "Vessels" },
  { key: "products", label: "Products" },
  { key: "tenders", label: "Tenders" },
  { key: "projects", label: "Projects" },
];

export default function Staff() {
  const confirm = useConfirm();
  const [staff, setStaff] = useState([]);
  const [registeredClients, setRegisteredClients] = useState([]);
  const [invites, setInvites] = useState([]);
  const [pendingRegs, setPendingRegs] = useState([]);
  const [resendingId, setResendingId] = useState(null);

  const resendInvite = async (inv) => {
    setResendingId(inv.id);
    try {
      const { data } = await api.post(`/invites/${inv.id}/resend`);
      if (data.email_sent === false) {
        toast.error(
          `Email could NOT be sent — copy the invite link and share it manually. ${data.email_error || ""}`,
          { duration: 12000 }
        );
      } else {
        toast.success(`Invite re-sent to ${inv.email} (link valid another 14 days)`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setResendingId(null);
    }
  };

  const decideRegistration = async (id, action) => {
    try {
      const { data } = await api.post(`/registrations/${id}/decide`, { action });
      toast.success(data.message);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "staff", permissions: [], can_edit: false });
  const [lastLink, setLastLink] = useState("");
  // Track the invite we just sent so the "Copy email" button can personalise
  // the body with the recipient's name + role-appropriate copy.
  const [lastInvite, setLastInvite] = useState(null);
  // Edit-user dialog. `editing` holds the user being edited (or null when
  // closed); `editForm` mirrors the fields the admin can change.
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", permissions: [], can_edit: false });
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({
      name: u.name || "",
      permissions: u.permissions || [],
      can_edit: !!u.can_edit,
    });
  };

  const togglePerm = (p) => {
    setEditForm((f) => ({
      ...f,
      permissions: f.permissions.includes(p)
        ? f.permissions.filter((x) => x !== p)
        : [...f.permissions, p],
    }));
  };

  const saveEdit = async () => {
    if (!editing) return;
    setEditSaving(true);
    try {
      const payload = { name: editForm.name.trim() };
      // Only send permissions / can_edit when editing a STAFF user — clients
      // don't have either concept, so leaving them out keeps the payload tidy.
      if (editing.role === "staff") {
        payload.permissions = editForm.permissions;
        payload.can_edit = editForm.can_edit;
      }
      await api.put(`/staff/${editing.id}`, payload);
      toast.success("Access updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setEditSaving(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, i, c, p] = await Promise.all([
        api.get("/staff"),
        api.get("/invites"),
        api.get("/registered-clients"),
        api.get("/registrations/pending"),
      ]);
      setStaff(s.data);
      setInvites(i.data);
      setRegisteredClients(c.data);
      setPendingRegs(p.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sendInvite = async () => {
    if (!form.email || !form.name) {
      toast.error("Name and email required");
      return;
    }
    if (form.role === "staff" && form.permissions.length === 0) {
      toast.error("Pick at least one page this staff member can access");
      return;
    }
    try {
      const { data } = await api.post("/invites", form);
      setLastLink(data.invite_url);
      setLastInvite({
        email: form.email,
        name: form.name,
        role: form.role,
        invite_url: data.invite_url,
      });
      // Friendlier copy depending on the role we just sent.
      if (data.email_sent === false) {
        toast.error(
          `Invite created but the email could NOT be sent — copy the invite link and share it manually. ${data.email_error || ""}`,
          { duration: 12000 }
        );
      } else {
        const friendly =
          form.role === "client"
            ? `Client invite sent to ${form.email}`
            : `Invite sent to ${form.email}`;
        toast.success(friendly);
      }
      setForm({ email: "", name: "", role: form.role, permissions: [], can_edit: false });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const togglePermission = (key) => {
    setForm((f) => {
      const has = f.permissions.includes(key);
      return {
        ...f,
        permissions: has ? f.permissions.filter((p) => p !== key) : [...f.permissions, key],
      };
    });
  };

  const revoke = async (id) => {
    const ok = await confirm({
      title: "Revoke this invite?",
      description: "The invite link will stop working immediately.",
      confirmText: "Revoke",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/invites/${id}`);
      toast.success("Invite revoked");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const removeUser = async (u) => {
    const ok = await confirm({
      title: `Remove ${u.name || u.email}?`,
      description: "This will permanently remove their workspace access.",
      confirmText: "Remove",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/staff/${u.id}`);
      toast.success("User removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Invite link copied");
    } catch {
      toast.error("Unable to copy");
    }
  };

  // Build a ready-to-paste plain-text email body for the last invite we
  // sent — useful while Resend domain verification is pending: admin
  // generates the invite, hits "Copy email", pastes it into Outlook and
  // sends from their own inbox.
  const buildEmailBody = (inv) => {
    if (!inv) return "";
    const firstName = (inv.name || "").trim().split(/\s+/)[0] || "there";
    if (inv.role === "client") {
      return (
        `Hi ${firstName},\n\n` +
        `Southern Ocean Solutions is a specialist shipbuilder delivering ` +
        `steel and aluminium workboats, cargo vessels and custom marine ` +
        `solutions for commercial and government operators globally. We're ` +
        `delighted to welcome you on board.\n\n` +
        `Your private client portal is ready. From here you can browse our ` +
        `vessel catalogue, save favourites, and request a tailored quote in ` +
        `just a few clicks.\n\n` +
        `To get started, click the link below to set your password:\n` +
        `${inv.invite_url}\n\n` +
        `This link expires in 14 days and can only be used once.\n\n` +
        `Looking forward to working with you,\n` +
        `Michael Graham-McKenna\n` +
        `Southern Ocean Solutions`
      );
    }
    return (
      `Hi ${firstName},\n\n` +
      `You've been invited to join the Southern Ocean Solutions workspace.\n\n` +
      `Click the link below to set your password and get started:\n` +
      `${inv.invite_url}\n\n` +
      `This link expires in 14 days and can only be used once.\n\n` +
      `Cheers,\n` +
      `Michael Graham-McKenna\n` +
      `Southern Ocean Solutions`
    );
  };

  const copyEmailBody = async () => {
    const body = buildEmailBody(lastInvite);
    if (!body) return;
    try {
      await navigator.clipboard.writeText(body);
      toast.success("Email body copied — paste into Outlook");
    } catch {
      toast.error("Unable to copy");
    }
  };

  // Subject line that pairs with the email body — used when launching mailto:
  const emailSubject = (inv) => {
    if (!inv) return "";
    return inv.role === "client"
      ? "Welcome to Southern Ocean Solutions — your client portal is ready"
      : "You're invited to Southern Ocean Solutions";
  };

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-[1400px] mx-auto pb-24">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#0088CC] mb-2">
            Workspace Admin
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#011836]">
            Staff
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Invite teammates and manage workspace access.
          </p>
        </div>
        <Button
          onClick={() => {
            setForm({ email: "", name: "", role: "staff", permissions: [], can_edit: false });
            setLastLink("");
            setOpen(true);
          }}
          className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
          data-testid="invite-staff-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> Invite Staff
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="sos-surface rounded-lg" data-testid="staff-list">
          <div className="px-5 py-4 border-b border-[#DDE5EC] flex items-center gap-2">
            <UserCheck className="h-4 w-4 text-[#0088CC]" />
            <h2 className="font-display text-lg font-semibold text-[#011836]">Active Members</h2>
          </div>
          <div className="divide-y divide-[#DDE5EC]">
            {loading && (
              <div className="px-5 py-8 text-center text-sm text-[#5B6B7C]">Loading…</div>
            )}
            {!loading && staff.length === 0 && (
              <div className="px-5 py-8 text-center text-sm text-[#5B6B7C]">No members</div>
            )}
            {staff.map((u) => (
              <div
                key={u.id}
                className="px-5 py-3.5 flex items-center justify-between"
                data-testid={`staff-row-${u.id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#011836] truncate">
                    {u.name || u.email}
                  </div>
                  <div className="text-xs text-[#5B6B7C]">{u.email}</div>
                  {u.role === "staff" && (u.permissions || []).length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1 items-center">
                      <span
                        className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-display font-semibold ${
                          u.can_edit
                            ? "bg-[#FBBF24]/15 text-[#FBBF24] border border-[#FBBF24]/40"
                            : "bg-[#EDF1F5] text-[#5B6B7C] border border-[#C4D0DB]"
                        }`}
                        title={u.can_edit ? "Can create, edit and delete" : "View + ask only — no edits"}
                        data-testid={`user-edit-flag-${u.id}`}
                      >
                        {u.can_edit ? "Editor" : "View only"}
                      </span>
                      {u.permissions.map((p) => (
                        <span
                          key={p}
                          className="text-[9px] uppercase tracking-wider text-[#0088CC] bg-[#0088CC]/10 border border-[#0088CC]/30 px-1.5 py-0.5 rounded"
                        >
                          {p}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
                      u.role === "admin"
                        ? "bg-[#E6F2FA] text-[#0088CC] border border-[#0088CC]/60"
                        : "bg-white text-[#5B6B7C] border border-[#C4D0DB]"
                    }`}
                  >
                    {u.role}
                  </span>
                  {u.role !== "admin" && (
                    <>
                      <button
                        onClick={() => openEdit(u)}
                        className="p-1.5 rounded text-[#5B6B7C] hover:text-[#011836] hover:bg-[#EDF1F5]"
                        data-testid={`edit-staff-${u.id}`}
                        aria-label="Edit access"
                        title="Edit access"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => removeUser(u)}
                        className="p-1.5 rounded text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC]"
                        data-testid={`remove-staff-${u.id}`}
                        aria-label="Remove"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="sos-surface rounded-lg" data-testid="invites-list">
          <div className="px-5 py-4 border-b border-[#DDE5EC] flex items-center gap-2">
            <Mail className="h-4 w-4 text-[#0088CC]" />
            <h2 className="font-display text-lg font-semibold text-[#011836]">Pending Invites</h2>
          </div>
          <div className="divide-y divide-[#DDE5EC]">
            {invites.length === 0 && (
              <div className="px-5 py-8 text-center text-sm text-[#5B6B7C]">No pending invites</div>
            )}
            {invites.map((inv) => (
              <div
                key={inv.id}
                className="px-5 py-3.5 flex items-center justify-between gap-3"
                data-testid={`invite-row-${inv.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#011836] truncate">
                    {inv.name || inv.email}
                    {inv.used && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-[#10B981]">
                        accepted
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#5B6B7C] truncate">{inv.email}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C] mt-0.5">
                    {inv.role} · sent {formatDate(inv.created_at)}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {!inv.used && (
                    <>
                      <button
                        onClick={() => resendInvite(inv)}
                        disabled={resendingId === inv.id}
                        className="p-1.5 rounded text-[#5B6B7C] hover:text-[#0088CC] hover:bg-[#E6F2FA] disabled:opacity-40"
                        data-testid={`resend-invite-${inv.id}`}
                        aria-label="Resend email"
                        title="Resend invite email"
                      >
                        <Send className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => copy(inv.invite_url)}
                        className="p-1.5 rounded text-[#5B6B7C] hover:text-[#011836] hover:bg-[#EDF1F5]"
                        data-testid={`copy-invite-${inv.id}`}
                        aria-label="Copy link"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => revoke(inv.id)}
                        className="p-1.5 rounded text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC]"
                        data-testid={`revoke-invite-${inv.id}`}
                        aria-label="Revoke"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Self-registrations awaiting an approve/decline decision. */}
      {pendingRegs.length > 0 && (
        <section className="sos-surface rounded-lg mt-6 border-l-4 border-l-[#F59E0B]" data-testid="pending-registrations-list">
          <div className="px-5 py-4 border-b border-[#DDE5EC] flex items-center gap-2">
            <UserCheck className="h-4 w-4 text-[#F59E0B]" />
            <h2 className="font-display text-lg font-semibold text-[#011836]">Pending Registrations</h2>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{pendingRegs.length}</span>
          </div>
          <div className="divide-y divide-[#DDE5EC]">
            {pendingRegs.map((r) => (
              <div key={r.id} className="px-5 py-3.5 flex items-center justify-between gap-3 flex-wrap" data-testid={`pending-reg-row-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#011836] truncate">{r.name || r.email}</div>
                  <div className="text-xs text-[#5B6B7C] truncate">
                    {r.email}
                    {r.company ? ` · ${r.company}` : ""}
                    {r.phone ? ` · ${r.phone}` : ""}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C] mt-0.5">
                    requested {formatDate(r.created_at)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => decideRegistration(r.id, "approve")}
                    className="bg-[#10B981] hover:bg-[#0E9F71] text-white h-8 text-xs"
                    data-testid={`approve-reg-${r.id}`}
                  >
                    <Check className="h-3.5 w-3.5 mr-1" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => decideRegistration(r.id, "decline")}
                    className="border-[#FCA5A5] bg-white text-[#EF4444] hover:bg-red-50 h-8 text-xs"
                    data-testid={`decline-reg-${r.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Self-registered + admin-invited client accounts — separate panel
          so admins can review who's signed up via the public /register page
          or been brought in via an admin-sent invite. */}
      <section
        className="sos-surface rounded-lg mt-6"
        data-testid="registered-clients-list"
      >
        <div className="px-5 py-4 border-b border-[#DDE5EC] flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Users className="h-4 w-4 text-[#0088CC]" />
            <h2 className="font-display text-lg font-semibold text-[#011836]">
              Registered Clients
              <span className="ml-2 text-xs text-[#5B6B7C] font-normal">
                ({registeredClients.length})
              </span>
            </h2>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              setForm({ email: "", name: "", role: "client", permissions: [], can_edit: false });
              setLastLink("");
              setOpen(true);
            }}
            className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
            data-testid="invite-client-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Invite Client
          </Button>
        </div>
        <div className="divide-y divide-[#DDE5EC]">
          {!loading && registeredClients.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-[#5B6B7C]">
              No clients have signed up yet.
            </div>
          )}
          {registeredClients.map((c) => (
            <div
              key={c.id}
              className="px-5 py-3.5 flex items-center justify-between gap-3"
              data-testid={`registered-client-${c.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#011836] truncate">
                  {c.name || c.email}
                </div>
                <div className="text-xs text-[#5B6B7C] truncate">
                  {c.email}
                  {c.company && (
                    <>
                      <span className="mx-1.5 text-[#475569]">·</span>
                      {c.company}
                    </>
                  )}
                  {c.phone && (
                    <>
                      <span className="mx-1.5 text-[#475569]">·</span>
                      {c.phone}
                    </>
                  )}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-[#64748B] mt-0.5">
                  joined {formatDate(c.created_at)}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => openEdit(c)}
                  className="p-1.5 rounded text-[#5B6B7C] hover:text-[#011836] hover:bg-[#EDF1F5]"
                  data-testid={`edit-client-${c.id}`}
                  aria-label="Edit client name"
                  title="Edit client name"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => removeUser(c)}
                  className="p-1.5 rounded text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC]"
                  data-testid={`remove-client-${c.id}`}
                  aria-label="Revoke access"
                  title="Revoke this client's access"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-[#DDE5EC] text-[#011836]" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              <UserCog className="inline-block h-4 w-4 mr-2 text-[#0088CC]" />
              Invite teammate
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                data-testid="invite-name-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Role</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v })}
              >
                <SelectTrigger className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836]" data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === "staff" && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                  Pages this staff member can access
                </Label>
                <div className="mt-2 grid grid-cols-2 gap-2" data-testid="invite-permissions-grid">
                  {PAGE_PERMISSIONS.map((p) => {
                    const checked = form.permissions.includes(p.key);
                    return (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => togglePermission(p.key)}
                        className={`flex items-center justify-between gap-2 px-3 py-2 rounded border text-sm transition-colors ${
                          checked
                            ? "border-[#0088CC]/60 bg-[#0088CC]/[0.08] text-white"
                            : "border-[#DDE5EC] bg-white text-[#5B6B7C] hover:text-[#011836]"
                        }`}
                        data-testid={`invite-perm-${p.key}`}
                      >
                        <span>{p.label}</span>
                        {checked && <Check className="h-3.5 w-3.5 text-[#0088CC]" />}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-[#64748B] mt-2 leading-snug">
                  Staff and Settings are admin-only and always hidden from staff accounts.
                </p>
                {/* Edit-rights toggle. Unticked = view + ask only; ticked =
                    full create/edit/delete on the permitted sections. */}
                <label
                  className="mt-3 flex items-start gap-3 cursor-pointer rounded border border-[#FBBF24]/30 bg-[#FBBF24]/[0.04] p-3 hover:bg-[#FBBF24]/[0.08] transition-colors"
                  data-testid="invite-can-edit-row"
                >
                  <input
                    type="checkbox"
                    checked={!!form.can_edit}
                    onChange={(e) => setForm({ ...form, can_edit: e.target.checked })}
                    className="mt-0.5 h-4 w-4 accent-[#FBBF24]"
                    data-testid="invite-can-edit-checkbox"
                  />
                  <span>
                    <span className="block text-sm font-display font-semibold text-[#011836]">
                      Can edit
                    </span>
                    <span className="block text-[11px] text-[#5B6B7C] leading-snug mt-0.5">
                      {form.can_edit
                        ? "This staff member can create, edit and delete records on the pages above."
                        : "View-only: this staff member can browse and ask questions of documents, but cannot create, edit or delete anything."}
                    </span>
                  </span>
                </label>
              </div>
            )}
            {form.role === "client" && (
              <div
                className="rounded border border-[#0088CC]/30 bg-[#0088CC]/[0.06] p-3 text-xs text-[#5B6B7C] leading-snug"
                data-testid="invite-client-hint"
              >
                <span className="font-display font-semibold text-[#0088CC] uppercase tracking-wider text-[10px]">
                  Client invite
                </span>
                <p className="mt-1 text-[#33475C]">
                  They&rsquo;ll receive a friendly welcome email with a link to set their password.
                  Once activated they can browse the vessel catalogue, save favourites, and request quotes.
                  No section permissions needed.
                </p>
              </div>
            )}
            {lastLink && (
              <div className="mt-3 p-3 rounded border border-[#0088CC]/40 bg-[#E6F2FA]">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-[#0088CC]">
                    Invite link {lastInvite ? `for ${lastInvite.email}` : ""}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-[#FBBF24] font-display font-semibold">
                    Send manually
                  </div>
                </div>
                <div className="text-xs text-[#011836] break-all" data-testid="last-invite-link">
                  {lastLink}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => copy(lastLink)}
                    className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
                    data-testid="copy-last-invite"
                  >
                    <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy link
                  </Button>
                  {lastInvite && (
                    <>
                      <Button
                        size="sm"
                        onClick={copyEmailBody}
                        variant="outline"
                        className="border-[#FBBF24]/40 text-[#FBBF24] hover:bg-[#FBBF24]/10"
                        data-testid="copy-last-invite-email"
                        title="Copy a ready-to-paste email body with the link + sign-off"
                      >
                        <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy email
                      </Button>
                      <a
                        href={`mailto:${encodeURIComponent(lastInvite.email)}?subject=${encodeURIComponent(emailSubject(lastInvite))}&body=${encodeURIComponent(buildEmailBody(lastInvite))}`}
                        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-sm font-medium border border-[#0088CC]/40 text-[#0088CC] hover:bg-[#0088CC]/10 transition-colors"
                        data-testid="open-mailto-last-invite"
                      >
                        <Send className="h-3.5 w-3.5" /> Open in mail app
                      </a>
                    </>
                  )}
                </div>
                <div className="mt-2 text-[10px] text-[#5B6B7C] leading-relaxed">
                  Email auto-delivery via Resend is pending domain verification.
                  Copy the link or email body and send from your own inbox.
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5]"
            >
              Close
            </Button>
            <Button
              onClick={sendInvite}
              className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
              data-testid="send-invite-btn"
            >
              Create invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user (access) dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent
          className="bg-[#F6F7F8] border-[#DDE5EC] max-w-md"
          data-testid="edit-user-modal"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#011836]">
              {editing?.role === "staff" ? "Edit staff access" : "Edit client"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-5">
              <div className="rounded-lg border border-[#DDE5EC] bg-white p-3">
                <div className="text-sm font-semibold text-[#011836]">{editing.email}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C] mt-0.5">
                  {editing.role}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#011836] text-xs uppercase tracking-wider">Full name</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="bg-white border-[#C4D0DB] text-[#011836]"
                  data-testid="edit-user-name-input"
                />
              </div>

              {editing.role === "staff" && (
                <>
                  <div className="space-y-2">
                    <Label className="text-[#011836] text-xs uppercase tracking-wider">
                      Page access
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      {PAGE_PERMISSIONS.map((perm) => {
                        const checked = editForm.permissions.includes(perm.key);
                        return (
                          <label
                            key={perm.key}
                            className={`flex items-center gap-2 rounded border px-3 py-2 cursor-pointer ${
                              checked
                                ? "border-[#0088CC] bg-[#E6F2FA]"
                                : "border-[#C4D0DB] bg-white hover:border-[#475569]"
                            }`}
                            data-testid={`edit-perm-${perm.key}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => togglePerm(perm.key)}
                              className="h-4 w-4 accent-[#0088CC]"
                            />
                            <span className="text-sm text-[#011836]">{perm.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <label
                    className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${
                      editForm.can_edit
                        ? "border-[#FBBF24]/60 bg-[#FFF7E6]"
                        : "border-[#C4D0DB] bg-white hover:border-[#475569]"
                    }`}
                    data-testid="edit-can-edit-toggle"
                  >
                    <input
                      type="checkbox"
                      checked={editForm.can_edit}
                      onChange={(e) =>
                        setEditForm({ ...editForm, can_edit: e.target.checked })
                      }
                      className="h-4 w-4 mt-0.5 accent-[#FBBF24]"
                    />
                    <div className="text-sm">
                      <div className="font-display font-semibold text-[#011836]">
                        Editor permissions
                      </div>
                      <div className="text-xs text-[#5B6B7C] mt-1">
                        Allow this person to create, edit and delete records on the pages they
                        can access. Leave unchecked for read-only access (they can still ask AI
                        and download files).
                      </div>
                    </div>
                  </label>
                </>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setEditing(null)}
              className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5]"
              data-testid="edit-user-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={saveEdit}
              disabled={editSaving || !editForm.name.trim()}
              className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
              data-testid="edit-user-save-btn"
            >
              {editSaving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
